import { request } from '../request';

/**
 * 创建发行申请草稿
 * POST /applications
 *
 * @param data 请求体参数 (Step1Request)
 */
export function fetchCreateApplicationDraft(data?: Api.Application.Step1Request) {
  return request<Api.Application.ApplicationResponse>({
    url: '/applications',
    method: 'post',
    data
  });
}

/**
 * 获取我的发行申请详情
 * GET /applications/{id}
 *
 * @param id 申请 ID
 */
export function fetchGetApplicationDetail(id: number | string) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}`,
    method: 'get'
  });
}

/**
 * 复制发行申请为新草稿
 * POST /applications/{id}/copy
 *
 * @param id 申请 ID
 */
export function fetchCopyApplication(id: number | string) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/copy`,
    method: 'post'
  });
}

/**
 * 保存发行申请步骤四添加授权链节点
 * POST /applications/{id}/copyright/auth-chain
 *
 * @param id 申请 ID
 * @param data 授权链节点请求数据 (AuthChainRequest)
 */
export function fetchAddAuthChainNode(id: number | string, data: Api.Application.AuthChainRequest) {
  return request<Api.Application.AuthChainResponse>({
    url: `/applications/${id}/copyright/auth-chain`,
    method: 'post',
    data
  });
}

/**
 * 保存发行申请步骤四删除授权链节点
 * DELETE /applications/{id}/copyright/auth-chain/{nodeId}
 *
 * @param id 申请 ID
 * @param nodeId 节点 ID
 */
export function fetchDeleteAuthChainNode(id: number | string, nodeId: number | string) {
  return request<Api.Application.DeleteResponse>({
    url: `/applications/${id}/copyright/auth-chain/${nodeId}`,
    method: 'delete'
  });
}

/**
 * 保存发行申请步骤四更新授权链节点
 * PUT /applications/{id}/copyright/auth-chain/{nodeId}
 *
 * @param id 申请 ID
 * @param nodeId 节点 ID
 * @param data 授权链节点更新数据 (AuthChainRequest)
 */
export function fetchUpdateAuthChainNode(
  id: number | string,
  nodeId: number | string,
  data: Api.Application.AuthChainRequest
) {
  return request<Api.Application.AuthChainResponse>({
    url: `/applications/${id}/copyright/auth-chain/${nodeId}`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤四保存权属声明
 * POST /applications/{id}/copyright/declaration
 *
 * @param id 申请 ID
 * @param data 权属声明请求参数 (Step4Request)
 */
export function fetchSaveCopyrightDeclaration(id: number | string, data: Api.Application.Step4Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/copyright/declaration`,
    method: 'post',
    data
  });
}

/**
 * 保存发行申请步骤三替换单集文件并保留历史版本
 * PUT /applications/{id}/episodes/{epNo}/replace
 *
 * @param id 申请 ID
 * @param epNo 集数
 * @param data 替换文件请求参数 (ReplaceEpisodeRequest)
 */
export function fetchReplaceEpisodeFile(
  id: number | string,
  epNo: number | string,
  data: Api.Application.ReplaceEpisodeRequest
) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/episodes/${epNo}/replace`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤三获取单集文件历史版本
 * GET /applications/{id}/episodes/{epNo}/versions
 *
 * @param id 申请 ID
 * @param epNo 集数
 */
export function fetchGetEpisodeFileVersions(id: number | string, epNo: number | string) {
  return request<Api.Application.EpisodeFileVersionResponse[]>({
    url: `/applications/${id}/episodes/${epNo}/versions`,
    method: 'get'
  });
}

/**
 * 保存发行申请步骤三恢复单集历史文件版本
 * POST /applications/{id}/episodes/{epNo}/versions/{versionId}/restore
 *
 * @param id 申请 ID
 * @param epNo 集数
 * @param versionId 历史版本 ID
 */
export function fetchRestoreEpisodeFileVersion(
  id: number | string,
  epNo: number | string,
  versionId: number | string
) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/episodes/${epNo}/versions/${versionId}/restore`,
    method: 'post'
  });
}

/**
 * 保存发行申请步骤三批量保存剧集文件
 * POST /applications/{id}/episodes/batch
 *
 * @param id 申请 ID
 * @param data 剧集文件批量保存数据 (Step3Request)
 */
export function fetchBatchSaveEpisodes(id: number | string, data: Api.Application.Step3Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/episodes/batch`,
    method: 'post',
    data
  });
}

/**
 * 保存发行申请步骤三调整剧集排序
 * PUT /applications/{id}/episodes/sort
 *
 * @param id 申请 ID
 * @param data 排序数据 (SortEpisodesRequest)
 */
export function fetchSortEpisodes(id: number | string, data: Api.Application.SortEpisodesRequest) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/episodes/sort`,
    method: 'put',
    data
  });
}

/**
 * 获取我的发行申请审核历史
 * GET /applications/{id}/review-records
 *
 * @param id 申请 ID
 */
export function fetchGetApplicationReviewRecords(id: number | string) {
  return request<Api.Application.ApplicantApplicationReviewTimelineResponse>({
    url: `/applications/${id}/review-records`,
    method: 'get'
  });
}

/**
 * 查看我的发行申请审核结果
 * GET /applications/{id}/review-result
 *
 * @param id 申请 ID
 */
export function fetchGetApplicationReviewResult(id: number | string) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/review-result`,
    method: 'get'
  });
}

/**
 * 保存发行申请步骤一基础信息
 * PUT /applications/{id}/step1
 *
 * @param id 申请 ID
 * @param data 步骤一请求数据 (Step1Request)
 */
export function fetchSaveStep1(id: number | string, data: Api.Application.Step1Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step1`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤二封面 宣传物料 宣传片 海报
 * PUT /applications/{id}/step2
 *
 * @param id 申请 ID
 * @param data 步骤二请求数据 (Step2Request)
 */
export function fetchSaveStep2(id: number | string, data: Api.Application.Step2Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step2`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤三批量保存剧集文件
 * PUT /applications/{id}/step3
 *
 * @param id 申请 ID
 * @param data 步骤三请求数据 (Step3Request)
 */
export function fetchSaveStep3(id: number | string, data: Api.Application.Step3Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step3`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤四保存权属声明
 * PUT /applications/{id}/step4
 *
 * @param id 申请 ID
 * @param data 步骤四请求数据 (Step4Request)
 */
export function fetchSaveStep4(id: number | string, data: Api.Application.Step4Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step4`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤五保存发行意向
 * PUT /applications/{id}/step5
 *
 * @param id 申请 ID
 * @param data 步骤五请求数据 (Step5Request)
 */
export function fetchSaveStep5(id: number | string, data: Api.Application.Step5Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step5`,
    method: 'put',
    data
  });
}

/**
 * 保存发行申请步骤六保存确认声明
 * PUT /applications/{id}/step6
 *
 * @param id 申请 ID
 * @param data 步骤六请求数据 (Step6Request)
 */
export function fetchSaveStep6(id: number | string, data: Api.Application.Step6Request) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/step6`,
    method: 'put',
    data
  });
}

/**
 * 提交发行申请审核
 * POST /applications/{id}/submit
 *
 * @param id 申请 ID
 */
export function fetchSubmitApplication(id: number | string) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/submit`,
    method: 'post'
  });
}

/**
 * 按资料归属补充发行申请资料
 * POST /applications/{id}/supplement
 *
 * @param id 申请 ID
 * @param data 补充资料数据 (SupplementRequest)
 */
export function fetchSupplementApplication(id: number | string, data: Api.Application.SupplementRequest) {
  return request<Api.Application.ApplicationResponse>({
    url: `/applications/${id}/supplement`,
    method: 'post',
    data
  });
}

/**
 * 获取我的发行申请列表
 * GET /my/applications
 *
 * @param params 查询参数 (page, page_size)
 */
export function fetchGetMyApplications(params?: Api.Application.ApplicationSearchParams) {
  return request<Api.Application.ApplicationListPageResponse>({
    url: '/my/applications',
    method: 'get',
    params
  });
}

/**
 * 获取可选择的发行渠道
 * GET /channels
 */
export function fetchGetChannels() {
  return request<Api.Application.ChannelResponse[]>({
    url: '/channels',
    method: 'get'
  });
}

/**
 * 删除发行申请草稿
 * DELETE /applications/{id}
 *
 * @param id 申请 ID
 */
export function fetchDeleteApplication(id: number | string) {
  return request<Api.Application.DeleteResponse>({
    url: `/applications/${id}`,
    method: 'delete'
  });
}



